import React, { useCallback, useMemo, useState } from 'react'
import { PiArrowClockwise, PiSparkle } from 'react-icons/pi'
import { Link } from 'react-router-dom'
import { RecommendationsCard } from '../../../components/RecommendationsCard/RecommendationsCard'
import { PerformanceDashboardCard } from '../../../components/PerformanceDashboard'
import {
  demoResetSummary,
  type ActionSummaryData,
} from '../../../services/recommendations_api'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/authentication/useAuth'
import useApiService from '../../../services/api'
import { FbDropdown } from '../../../components/FbUI/FbDropdown'
import { secondaryPillButtonClass } from '../../../components/FbUI/secondaryPillButton'
import { ErrorBoundary } from '../../../components/UI/ErrorBoundary'
import { cn } from '../../../components/UI/cn'
import type { RightPanelState } from '../../../components/RecommendationsCard/RightPanel'
import confirm from '../../../components/dialogConfirm'

/**
 * Home page - Your daily recommendations and priorities
 *
 * Single centered column: greeting → performance → pipeline snapshot →
 * action center → manage-agents footer link. Width is constrained with
 * a max-width container so dashboards remain readable on wide displays.
 *
 * Superusers can select any user to view/generate their recommendations.
 */
export function HomePage() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>()
  const queryClient = useQueryClient()
  const { user, isAdmin, isLimited } = useAuth()
  const { data: users } = useApiService().useGetUsers(
    (!user?.is_superuser && !isAdmin) || isLimited
  )

  const isSuperuser = user?.is_superuser
  const canManageSummaries = isSuperuser || isAdmin

  // Local-time greeting: morning < 12, afternoon < 18, else evening.
  // Computed once per render rather than memoized — Date() is cheap and
  // we don't want a stale greeting if the user keeps the tab open
  // across the noon/6pm boundary (a re-render close to that time will
  // pick up the new band naturally).
  const greeting =
    new Date().getHours() < 12
      ? 'Good morning!'
      : new Date().getHours() < 18
        ? 'Good afternoon!'
        : 'Good evening!'

  const summaryQueryKey = useMemo(
    () => ['recommendations', selectedUserId ?? 'self'],
    [selectedUserId]
  )

  const handleDemoReset = async () => {
    const targetLabel = selectedUserId
      ? (selectedUser?.email ?? 'this user')
      : (user?.email ?? 'your account')
    const confirmed = await confirm(
      <>
        This wipes all recommendations for <strong>{targetLabel}</strong> and
        replays the demo seed in their place. Any existing recommendation state
        (applied actions, dismissals, in-flight LLM evaluations) is lost. Run
        only against demo accounts.
      </>,
      'Reset demo data?',
      'This cannot be undone.',
      { confirmLabel: 'Reset demo data', buttonVariant: 'danger' }
    )
    if (!confirmed) return

    setIsGenerating(true)
    try {
      await demoResetSummary(selectedUserId)
      // Invalidate to force a fresh fetch — the reset is synchronous so
      // the DB already has the new summary.
      await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
    } catch (error) {
      console.error('Failed to reset demo:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSummaryLoaded = useCallback(
    (summary: ActionSummaryData | null) => {
      if (summary?.status === 'COMPLETED' || summary?.status === 'FAILED') {
        setIsGenerating(false)
      }
    },
    []
  )

  const userDropdownItems = useMemo(() => {
    const others =
      users?.map((u) => ({
        key: String(u.id),
        label: `${u.first_name} ${u.last_name} (${u.email})`,
      })) ?? []
    // Superusers are excluded from the users/ endpoint — prepend self
    if (user && !others.find((o) => o.key === String(user.id))) {
      others.unshift({
        key: String(user.id),
        label: `${user.first_name} ${user.last_name} (${user.email}) (me)`,
      })
    }
    // "My Summary" resets the selector back to the current user's own view
    return [{ key: 'self', label: 'My Summary' }, ...others]
  }, [users, user])

  const selectedUser =
    users?.find((u) => u.id === selectedUserId) ??
    (selectedUserId === user?.id ? user : undefined)

  const selectedUserLabel = selectedUserId
    ? (userDropdownItems.find((o) => o.key === String(selectedUserId))?.label ??
      'My Summary')
    : 'My Summary'

  // Single panel-at-a-time state. Setting a new ``kind`` replaces
  // whatever was open. RecommendationsCard reads this and portals the
  // matching content into the host ref below.
  const [activePanel, setActivePanel] = useState<RightPanelState>({
    kind: 'closed',
  })
  const isPanelOpen = activePanel.kind !== 'closed'
  const resolvedSiderHostRef = React.useRef<HTMLDivElement>(null)

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 bg-neutral-100 overflow-hidden relative">
        {/* Scrollable content layer. Constrained to the relative
            wrapper so the sider that overlays it (below) doesn't
            scroll with content. */}
        <div className="absolute inset-0 overflow-auto">
          <div className="max-w-7xl mx-auto py-4 px-6 lg:px-8">
            <div className="space-y-4">
              {/* Page header card — greeting + page-level controls.
                  Kept separate from the performance card so each widget
                  has a single, clear purpose. */}
              <div className="bg-surface rounded-lg border border-line px-4 py-3 flex items-center justify-between gap-4">
                <h1 className="text-2xl font-normal text-fg">{greeting}</h1>
                <div className="flex items-center gap-2">
                  {isSuperuser && (
                    <button
                      type="button"
                      onClick={handleDemoReset}
                      disabled={isGenerating}
                      className="inline-flex items-center gap-1.5 h-[38px] px-3 text-sm font-medium rounded-md border border-error text-error hover:bg-error-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      title={
                        selectedUserId
                          ? `Reset demo data for ${selectedUser?.email}. This wipes their recommendations and seeds fresh fixtures.`
                          : 'Reset demo data — wipes recommendations and seeds fresh fixtures.'
                      }
                    >
                      <PiArrowClockwise
                        size={14}
                        className={isGenerating ? 'animate-spin' : ''}
                      />
                      Reset User Demo Data
                    </button>
                  )}
                  {canManageSummaries && users && (
                    <FbDropdown
                      label={selectedUserLabel}
                      items={userDropdownItems}
                      selectedKey={
                        selectedUserId !== undefined
                          ? String(selectedUserId)
                          : 'self'
                      }
                      onAction={(key) => {
                        if (key === 'self') {
                          setSelectedUserId(undefined)
                        } else {
                          const id = Number(key)
                          setSelectedUserId(id === user?.id ? undefined : id)
                        }
                        setIsGenerating(false)
                      }}
                      placement="bottom end"
                    />
                  )}
                </div>
              </div>

              <PerformanceDashboardCard userId={selectedUserId} />

              <div className="bg-surface rounded-lg border border-line p-4">
                <ErrorBoundary
                  fallback={
                    <div className="rounded-lg border border-red-200 bg-red-50 p-6">
                      <p className="text-red-800 font-medium">
                        Failed to load recommendations
                      </p>
                      <p className="mt-1 text-sm text-red-600">
                        Please try refreshing the page
                      </p>
                    </div>
                  }
                >
                  <RecommendationsCard
                    userId={selectedUserId}
                    onSummaryLoaded={handleSummaryLoaded}
                    activePanel={activePanel}
                    onActivePanelChange={setActivePanel}
                    resolvedSiderHostRef={resolvedSiderHostRef}
                    headerSlot={
                      <div>
                        <h2 className="text-2xl font-semibold text-fg">
                          Action Center
                        </h2>
                        <p className="mt-0.5 text-sm text-fg-secondary">
                          Here&apos;s what needs your attention
                        </p>
                      </div>
                    }
                    headerTrailingSlot={
                      <Link
                        to="/settings/ai"
                        className={secondaryPillButtonClass}
                      >
                        <PiSparkle
                          size={14}
                          className="text-brand-active flex-shrink-0"
                        />
                        Manage AI Agents
                      </Link>
                    }
                  />
                </ErrorBoundary>
              </div>
            </div>
          </div>
        </div>

        {/* Dimming backdrop — sits between the scrollable content and
            the overlay sider. Click to close. Mounted only when the
            sider is open so it doesn't intercept clicks otherwise. */}
        {isPanelOpen && (
          <div
            className="absolute inset-0 bg-black/30 transition-opacity duration-300 z-10"
            onClick={() => setActivePanel({ kind: 'closed' })}
            aria-hidden="true"
          />
        )}

        {/* Recently-completed sider host. Overlays the right side of
            the content area (no flex-sibling squish). Always rendered
            so the portal target is available; width animates between
            0 and 945px (capped to viewport via ``max-w-full`` on
            narrower screens). Sits above the backdrop. */}
        <div
          ref={resolvedSiderHostRef}
          className={cn(
            'absolute top-0 right-0 bottom-0 overflow-hidden bg-surface transition-[width] duration-300 ease-out z-20 max-w-full',
            isPanelOpen
              ? 'w-[945px] border-l border-line shadow-2xl'
              : 'w-0 border-l-0'
          )}
        />
      </div>
    </div>
  )
}
